import * as React from 'react';
import { Alert } from 'reactstrap';
import { AxiosError } from 'axios';
import { WithTestIdOptional } from 'shared-client/types/test';

export interface ErrorAlertProps extends WithTestIdOptional {
	/** The title of the error alert */
	title: string;

	/** The message to display */
	message?: string;

	/** The error responsible for this message. */
	error: AxiosError | Error | null;
}

/**
 * Interface that mirrors the backend's RocApiError class
 */
export interface RocApiError {
	message: string;
	loggingErrorId?: string;
	exception?: Exception;
}

interface Exception {
	stackTrace?: string;
}

const GenericErrorMessage =
	'An unexpected error has occurred. Please try again. If the problem persists, contact a system administrator.';

const NetworkErrorMessage =
	'A network error has occurred. Check your network connection and if the problem persists, contact a system administrator.';

/**
 * Reusable Error Alert Component used by AdminErrorBoundary and Listing/Tree base components.
 *
 * @class ErrorAlert
 * @extends {React.Component<ErrorAlertProps>}
 */
class ErrorAlert extends React.Component<ErrorAlertProps> {
	public static defaultProps = {
		title: 'Uh oh!',
	};

	/**
	 * Gets the message to display to the end user.
	 */
	private getMessage(): string {
		if (this.props.message) {
			return this.props.message;
		}
		const { error } = this.props;
		const axiosError = error as AxiosError | undefined;
		if (axiosError && axiosError.response == null && axiosError.request != null) {
			return NetworkErrorMessage;
		}

		// Handle error messages sent from the server
		if (
			axiosError &&
			axiosError.request != null &&
			axiosError.response != null &&
			axiosError.response.data != null &&
			axiosError.response.data.message != null
		) {
			const error = axiosError.response.data as RocApiError;
			return error.message;
		}

		// More specific error messages can be placed here (such as for a 403 or similar error code).

		return GenericErrorMessage;
	}

	public render() {
		const { error } = this.props;
		const axiosError = error as AxiosError | null;
		const loggingErrorId: string | undefined =
			axiosError && axiosError.response && axiosError.response.data && axiosError.response.data.loggingErrorId;

		return (
			<Alert color="danger" data-testid={this.props.testId}>
				<strong>{this.props.title}</strong> {this.getMessage()}
				{this.props.children ? <> {this.props.children}</> : null}
				{loggingErrorId ? <p>ID: {loggingErrorId}</p> : null}
			</Alert>
		);
	}
}

export default ErrorAlert;
